import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { PageHeader, Alert } from '@components/common'
import {
	ProfileCard,
	ProfileTabs,
	ProfileInfoTab,
	ProfileSecurityTab,
	ProfileAccountTab,
} from '@components/features/profile'
import { profileService } from '@services/profileService'
import authService from '@services/authService'
import './Profile.css'

/**
 * User Profile Page
 *
 * Complete profile management for authenticated users.
 *
 * Features:
 * - View profile information
 * - Edit personal details (first name, last name, phone)
 * - Upload/remove avatar
 * - Change password with strength validation
 * - View read-only account information
 */
export default function Profile() {
	// State
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [activeTab, setActiveTab] = useState('info')

	// Operation states
	const [avatarLoading, setAvatarLoading] = useState(false)
	const [profileSaving, setProfileSaving] = useState(false)
	const [passwordSaving, setPasswordSaving] = useState(false)

	/**
	 * Fetch user profile data
	 */
	const fetchProfile = useCallback(async () => {
		try {
			setLoading(true)
			setError(null)
			const data = await profileService.getProfile()
			setUser(data)
		} catch (err) {
			console.error('Failed to fetch profile:', err)
			setError(err.message || 'Failed to load profile')
		} finally {
			setLoading(false)
		}
	}, [])

	// Initial load
	useEffect(() => {
		fetchProfile()
	}, [fetchProfile])

	/**
	 * Handle avatar upload
	 */
	const handleAvatarUpload = async (file) => {
		try {
			setAvatarLoading(true)

			// Validate file size (2MB)
			if (file.size > 2 * 1024 * 1024) {
				throw new Error('File size must be less than 2MB')
			}

			// Validate file type
			const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
			if (!validTypes.includes(file.type)) {
				throw new Error('Please select a valid image file (JPG, PNG, GIF, or WebP)')
			}

			const result = await profileService.uploadAvatar(file, (progress) => {
				// Optional: show upload progress
				console.log(`Upload progress: ${progress}%`)
			})

			// Update local state
			setUser((prev) => ({
				...prev,
				avatar: result.avatar,
			}))

			// Update auth service cache
			const currentUser = authService.getCurrentUser()
			if (currentUser) {
				authService.setUserData(
					{ ...currentUser, avatar: result.avatar },
					authService.isTokenPersistent()
				)
			}

			toast.success('Avatar uploaded successfully')
		} catch (err) {
			console.error('Avatar upload failed:', err)
			toast.error(err.message || 'Failed to upload avatar')
		} finally {
			setAvatarLoading(false)
		}
	}

	/**
	 * Handle avatar removal
	 */
	const handleAvatarRemove = async () => {
		try {
			setAvatarLoading(true)
			await profileService.removeAvatar()

			// Update local state
			setUser((prev) => ({
				...prev,
				avatar: null,
			}))

			// Update auth service cache
			const currentUser = authService.getCurrentUser()
			if (currentUser) {
				authService.setUserData(
					{ ...currentUser, avatar: null },
					authService.isTokenPersistent()
				)
			}

			toast.success('Avatar removed')
		} catch (err) {
			console.error('Avatar removal failed:', err)
			toast.error(err.message || 'Failed to remove avatar')
		} finally {
			setAvatarLoading(false)
		}
	}

	/**
	 * Handle profile update
	 */
	const handleProfileSave = async (data) => {
		try {
			setProfileSaving(true)
			const updatedUser = await profileService.updateProfile(data)
			setUser(updatedUser)

			// Update auth service cache
			const currentUser = authService.getCurrentUser()
			if (currentUser) {
				authService.setUserData(
					{
						...currentUser,
						first_name: updatedUser.first_name,
						last_name: updatedUser.last_name,
						phone: updatedUser.phone,
					},
					authService.isTokenPersistent()
				)
			}

			toast.success('Profile updated successfully')
		} catch (err) {
			console.error('Profile update failed:', err)
			throw err // Re-throw for component handling
		} finally {
			setProfileSaving(false)
		}
	}

	/**
	 * Handle password change
	 */
	const handlePasswordChange = async (currentPassword, newPassword, confirmPassword) => {
		try {
			setPasswordSaving(true)
			await profileService.changePassword(currentPassword, newPassword, confirmPassword)
			toast.success('Password changed successfully')
		} catch (err) {
			console.error('Password change failed:', err)
			throw err // Re-throw for component handling
		} finally {
			setPasswordSaving(false)
		}
	}

	/**
	 * Render tab content based on active tab
	 */
	const renderTabContent = () => {
		switch (activeTab) {
			case 'info':
				return (
					<ProfileInfoTab
						user={user}
						onSave={handleProfileSave}
						saving={profileSaving}
					/>
				)
			case 'security':
				return (
					<ProfileSecurityTab
						onChangePassword={handlePasswordChange}
						saving={passwordSaving}
					/>
				)
			case 'account':
				return <ProfileAccountTab user={user} />
			default:
				return null
		}
	}

	// Loading state
	if (loading) {
		return (
			<div className="profile-page">
				<PageHeader
					title="My Profile"
					description="Manage your personal information and settings"
				/>
				<div className="profile-page__loading">
					<Loader2 size={32} className="profile-page__spinner" />
					<span>Loading profile...</span>
				</div>
			</div>
		)
	}

	// Error state
	if (error) {
		return (
			<div className="profile-page">
				<PageHeader
					title="My Profile"
					description="Manage your personal information and settings"
				/>
				<Alert
					variant="error"
					message={error}
					dismissible
					onClose={() => setError(null)}
				/>
			</div>
		)
	}

	return (
		<div className="profile-page">
			<PageHeader
				title="My Profile"
				description="Manage your personal information and settings"
			/>

			<div className="profile-page__content">
				{/* Left Column - Profile Card */}
				<aside className="profile-page__sidebar">
					<ProfileCard
						user={user}
						onAvatarUpload={handleAvatarUpload}
						onAvatarRemove={handleAvatarRemove}
						avatarLoading={avatarLoading}
					/>
				</aside>

				{/* Right Column - Tabbed Content */}
				<main className="profile-page__main">
					<ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} />
					<div className="profile-page__tab-content">{renderTabContent()}</div>
				</main>
			</div>
		</div>
	)
}
